/**
 * Lógica de negócio do checklist: CRUD em memória.
 * Persistência é feita via API (checklist-api.ts).
 */

import type {
  ChecklistState,
  ChecklistTopic,
  ChecklistItem,
  ChecklistSubitem,
  ChecklistProgress,
} from '@/types/checklist'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

// --- Progresso ---

export function getChecklistProgress(state: ChecklistState): ChecklistProgress {
  let completed = 0
  let total = 0
  for (const topic of state.topics) {
    for (const item of topic.items) {
      total += 1
      if (item.completed) completed += 1
      for (const sub of item.subitems) {
        total += 1
        if (sub.completed) completed += 1
      }
    }
  }
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
  return { completed, total, percentage }
}

// --- Tópicos ---

export function addTopic(state: ChecklistState): ChecklistState {
  const minOrder =
    state.topics.length > 0
      ? Math.min(...state.topics.map((t) => t.order))
      : 0
  const newTopic: ChecklistTopic = {
    id: generateId(),
    title: 'Novo tópico',
    order: minOrder - 1,
    items: [],
  }
  return {
    ...state,
    topics: [...state.topics, newTopic].sort((a, b) => a.order - b.order),
  }
}

export function updateTopic(
  state: ChecklistState,
  topicId: string,
  updates: Partial<Pick<ChecklistTopic, 'title'>>
): ChecklistState {
  return {
    ...state,
    topics: state.topics.map((t) =>
      t.id === topicId ? { ...t, ...updates } : t
    ),
  }
}

export function removeTopic(
  state: ChecklistState,
  topicId: string
): ChecklistState {
  return {
    ...state,
    topics: state.topics.filter((t) => t.id !== topicId),
  }
}

export function moveTopic(
  state: ChecklistState,
  topicId: string,
  direction: 'up' | 'down'
): ChecklistState {
  const idx = state.topics.findIndex((t) => t.id === topicId)
  if (idx < 0) return state
  const nextIdx = direction === 'up' ? idx - 1 : idx + 1
  if (nextIdx < 0 || nextIdx >= state.topics.length) return state
  const topics = state.topics.slice()
  const a = topics[idx]
  const b = topics[nextIdx]
  const aOrder = a.order
  const bOrder = b.order
  topics[idx] = { ...a, order: bOrder }
  topics[nextIdx] = { ...b, order: aOrder }
  return { ...state, topics: topics.sort((x, y) => x.order - y.order) }
}

// --- Itens ---

export function addItem(
  state: ChecklistState,
  topicId: string
): ChecklistState {
  const topic = state.topics.find((t) => t.id === topicId)
  if (!topic) return state
  const maxOrder =
    topic.items.length > 0
      ? Math.max(...topic.items.map((i) => i.order), 0)
      : -1
  const newItem: ChecklistItem = {
    id: generateId(),
    topicId,
    text: 'Novo item',
    completed: false,
    order: maxOrder + 1,
    subitems: [],
  }
  return {
    ...state,
    topics: state.topics.map((t) =>
      t.id === topicId
        ? {
            ...t,
            items: [...t.items, newItem].sort((a, b) => a.order - b.order),
          }
        : t
    ),
  }
}

export function updateItem(
  state: ChecklistState,
  itemId: string,
  updates: Partial<Pick<ChecklistItem, 'text' | 'completed' | 'dueDate' | 'note'>>
): ChecklistState {
  return {
    ...state,
    topics: state.topics.map((t) => ({
      ...t,
      items: t.items.map((i) =>
        i.id === itemId ? { ...i, ...updates } : i
      ),
    })),
  }
}

export function removeItem(
  state: ChecklistState,
  itemId: string
): ChecklistState {
  return {
    ...state,
    topics: state.topics.map((t) => ({
      ...t,
      items: t.items.filter((i) => i.id !== itemId),
    })),
  }
}

export function moveItem(
  state: ChecklistState,
  topicId: string,
  itemId: string,
  direction: 'up' | 'down'
): ChecklistState {
  const topic = state.topics.find((t) => t.id === topicId)
  if (!topic) return state
  const idx = topic.items.findIndex((i) => i.id === itemId)
  if (idx < 0) return state
  const nextIdx = direction === 'up' ? idx - 1 : idx + 1
  if (nextIdx < 0 || nextIdx >= topic.items.length) return state
  const items = topic.items.slice()
  const a = items[idx]
  const b = items[nextIdx]
  const aOrder = a.order
  const bOrder = b.order
  items[idx] = { ...a, order: bOrder }
  items[nextIdx] = { ...b, order: aOrder }
  return {
    ...state,
    topics: state.topics.map((t) =>
      t.id === topicId
        ? { ...t, items: items.sort((x, y) => x.order - y.order) }
        : t
    ),
  }
}

// --- Subitens ---

export function addSubitem(
  state: ChecklistState,
  itemId: string
): ChecklistState {
  for (const topic of state.topics) {
    const item = topic.items.find((i) => i.id === itemId)
    if (!item) continue
    const maxOrder =
      item.subitems.length > 0
        ? Math.max(...item.subitems.map((s) => s.order), 0)
        : -1
    const newSub: ChecklistSubitem = {
      id: generateId(),
      itemId,
      text: 'Novo subitem',
      completed: false,
      order: maxOrder + 1,
    }
    return {
      ...state,
      topics: state.topics.map((t) => ({
        ...t,
        items: t.items.map((i) =>
          i.id === itemId
            ? {
                ...i,
                subitems: [...i.subitems, newSub].sort(
                  (a, b) => a.order - b.order
                ),
              }
            : i
        ),
      })),
    }
  }
  return state
}

export function updateSubitem(
  state: ChecklistState,
  subitemId: string,
  updates: Partial<Pick<ChecklistSubitem, 'text' | 'completed'>>
): ChecklistState {
  return {
    ...state,
    topics: state.topics.map((t) => ({
      ...t,
      items: t.items.map((i) => ({
        ...i,
        subitems: i.subitems.map((s) =>
          s.id === subitemId ? { ...s, ...updates } : s
        ),
      })),
    })),
  }
}

export function removeSubitem(
  state: ChecklistState,
  subitemId: string
): ChecklistState {
  return {
    ...state,
    topics: state.topics.map((t) => ({
      ...t,
      items: t.items.map((i) => ({
        ...i,
        subitems: i.subitems.filter((s) => s.id !== subitemId),
      })),
    })),
  }
}

export function moveSubitem(
  state: ChecklistState,
  itemId: string,
  subitemId: string,
  direction: 'up' | 'down'
): ChecklistState {
  for (const topic of state.topics) {
    const item = topic.items.find((i) => i.id === itemId)
    if (!item) continue
    const idx = item.subitems.findIndex((s) => s.id === subitemId)
    if (idx < 0) return state
    const nextIdx = direction === 'up' ? idx - 1 : idx + 1
    if (nextIdx < 0 || nextIdx >= item.subitems.length) return state
    const subitems = item.subitems.slice()
    const a = subitems[idx]
    const b = subitems[nextIdx]
    const aOrder = a.order
    const bOrder = b.order
    subitems[idx] = { ...a, order: bOrder }
    subitems[nextIdx] = { ...b, order: aOrder }
    return {
      ...state,
      topics: state.topics.map((t) => ({
        ...t,
        items: t.items.map((i) =>
          i.id === itemId
            ? {
                ...i,
                subitems: subitems.sort((x, y) => x.order - y.order),
              }
            : i
        ),
      })),
    }
  }
  return state
}
